import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { HiChevronUpDown } from 'react-icons/hi2'
import { validateDate } from '../../../features/forms/validators'
import { useForm } from 'react-hook-form'
import MyInput from '../../../features/forms/MyInput'
import MySelect from '../../../features/forms/MySelect'
import { selectCurrentChannel, selectCurrentChannelAccount } from '../integrationsSlice'
import { selectRootAccounts, selectRootUser } from '../../../rootSlice'
import { fetchBackfill } from '../../../actions/backFillActions'
import { accountEmailsDiffer, accountPickerLabel, isSameAccount } from '../accountDisplay'

/**
 *
 * Select field options
 *
 */
const backfillTypes = [
  {
    type: 'last',
    title: 'Last',
    formProps: {
      name: 'lastDays',
      options: [
        { label: 'All History', value: 'all' },
        { label: '1 day', value: 1 },
        { label: '7 days', value: 7 },
        { label: '14 days', value: 14 },
        { label: '30 days', value: 30 },
        { label: '90 days', value: 90 },
        { label: '1 year', value: 365 },
        { label: '2 years', value: 730 },
      ],
      rules: {},
    },
  },
  {
    type: 'back_to',
    title: 'Back to',
    formProps: {
      name: 'backToDate',
      placeholder: 'YYYY-MM-DD',
      rules: { required: 'Required', validate: validateDate },
    },
  },
  {
    type: 'until',
    title: 'Until',
    formProps: {
      name: 'untilDate',
      placeholder: 'YYYY-MM-DD',
      rules: { required: 'Required', validate: validateDate },
    },
  },
  {
    type: 'on',
    title: 'On',
    formProps: { name: 'onDate', placeholder: 'YYYY-MM-DD', rules: { required: 'Required', validate: validateDate } },
  },
  {
    type: 'orderId',
    title: 'Order ID',
    formProps: { name: 'orderId', placeholder: '10012345', rules: { required: 'Required' } },
  },
  {
    type: 'product',
    title: 'Product',
    formProps: { name: 'product', disabled: true, values: 'product' },
  },
  {
    type: 'payout_totals',
    title: 'Payout Last Days',
    formProps: {
      name: 'lastDays',
      options: [
        { label: '1 day', value: 1 },
        { label: '7 days', value: 7 },
        { label: '14 days', value: 14 },
        { label: '30 days', value: 30 },
        { label: '90 days', value: 90 },
        { label: '1 year', value: 365 },
        { label: '2 years', value: 730 },
      ],
      rules: {},
    },
  },
  {
    type: 'payout_type',
    title: 'Payout On Date',
    formProps: { name: 'onDate', placeholder: 'YYYY-MM-DD', rules: { required: 'Required', validate: validateDate } },
  },
]

export default function BackfillModal({ visible, closeModal }) {
  const dispatch = useDispatch()

  /**
   *
   * Handle accounts
   *
   */
  const currentChannel = useSelector(selectCurrentChannel)
  const currentChannelAccount = useSelector(selectCurrentChannelAccount)
  const rootAccounts = useSelector(selectRootAccounts)
  const user = useSelector(selectRootUser)
  const isMimicUser = user?.is_mimic

  const availableAccounts = useMemo(() => {
    const fromIntegration = Array.isArray(currentChannel?.accounts) ? currentChannel.accounts : []
    if (fromIntegration.length > 0) return fromIntegration
    const ch = currentChannelAccount?.channel
    if (ch) return rootAccounts.filter((a) => a.channel === ch)
    return []
  }, [currentChannel, currentChannelAccount, rootAccounts])

  const showAccountEmail = useMemo(() => accountEmailsDiffer(availableAccounts), [availableAccounts])

  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0)

  // Preselect the account whose row the Backfill button was clicked on, once per open.
  const didPreselectRef = useRef(false)
  useEffect(() => {
    if (!visible) {
      didPreselectRef.current = false
      return
    }
    if (didPreselectRef.current || availableAccounts.length === 0) return
    didPreselectRef.current = true
    const matched = availableAccounts.findIndex((a) => isSameAccount(a, currentChannelAccount))
    setSelectedAccountIndex(matched >= 0 ? matched : 0)
  }, [visible, availableAccounts, currentChannelAccount])

  useEffect(() => {
    if (!visible || availableAccounts.length === 0) return
    setSelectedAccountIndex((i) => Math.min(Math.max(0, i), availableAccounts.length - 1))
  }, [visible, availableAccounts.length])

  const safeIndex =
    availableAccounts.length > 0 ? Math.min(selectedAccountIndex, availableAccounts.length - 1) : 0
  const selectedAccount = availableAccounts.length > 0 ? availableAccounts[safeIndex] : null

  /**
   *
   * Handle backfill options
   *
   */
  let backfillTypeOptions = backfillTypes.map(({ type, title }) => ({ value: type, label: title }))

  if (selectedAccount?.channel === 'Etsy') {
    backfillTypeOptions = backfillTypes.map(({ type, title }) => ({ value: type, label: title })).filter(({ value }) => value !== 'orderId')
  }
  else if (selectedAccount?.channel === 'Amazon') {
    backfillTypeOptions = backfillTypes.map(({ type, title }) => ({ value: type, label: title })).filter(({ value }) => !['orderId', 'product'].includes(value))
  }
  else if (selectedAccount?.channel === 'Wix') {
    backfillTypeOptions = backfillTypes.map(({ type, title }) => ({ value: type, label: title })).filter(({ value }) => value !== 'product')
  }

  // Filter payout options for non-Shopify channels (for all users including admins)
  if (selectedAccount?.channel !== 'Shopify') {
    backfillTypeOptions = backfillTypeOptions.filter(({ value }) => !['payout_totals', 'payout_type'].includes(value))
  }

  if (!isMimicUser) {
    backfillTypeOptions = backfillTypeOptions.filter(({ value }) => !['payout_totals', 'payout_type'].includes(value))
  }

  /**
   *
   * Handle form
   *
   */
  const {
    register,
    handleSubmit,
    watch,
    reset: resetForm,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      backfillType: backfillTypeOptions[0]?.value ?? 'last',
    },
  })

  /**
   *
   * Watch the "backfill type" field (first field) and change the "backfill options" field (second field)
   *
   */
  const watchBackfillType = watch('backfillType')
  const [backfillOptionsFormProps, setBackfillOptionsFormProps] = useState(null)
  useEffect(() => {
    const backfillType = backfillTypes.find(({ type: backfillType }) => backfillType === watchBackfillType)
    if (backfillType) {
      // Set the second field formProps
      setBackfillOptionsFormProps(backfillType.formProps)

      // Also remove form values from previous selections
      resetForm()
      setValue('backfillType', watchBackfillType)
    }
  }, [watchBackfillType])

  /**
   *
   * Handle form submit
   *
   */
  const onSubmit = (values) => {
    if (!selectedAccount) return

    let currentAccountChannelAccountId = selectedAccount.merchant_id || selectedAccount.account_name

    if (selectedAccount.channel === 'Etsy') {
      currentAccountChannelAccountId = selectedAccount.shop_id
    }

    if (values.lastDays) {
      if (values.lastDays === 'all') {
        // Special case for all history
        dispatch(fetchBackfill(currentAccountChannelAccountId, selectedAccount.channel))
      } else {
        dispatch(
          fetchBackfill(currentAccountChannelAccountId, selectedAccount.channel, {
            total: parseInt(values.lastDays),
            type: values.backfillType,
          })
        )
      }
    }

    if (values.backToDate || values.untilDate || values.onDate) {
      dispatch(
        fetchBackfill(currentAccountChannelAccountId, selectedAccount.channel, {
          date: values.backToDate || values.untilDate || values.onDate,
          type: values.backfillType,
        })
      )
    }

    if (values.orderId) {
      dispatch(
        fetchBackfill(currentAccountChannelAccountId, selectedAccount.channel, { order: values.orderId })
      )
    }

    if (values.backfillType === 'product') {
      dispatch(
        fetchBackfill(currentAccountChannelAccountId, selectedAccount.channel, { product: true })
      )
    }

    resetForm()
    closeModal()
  }

  return (
    <Modal show={visible} onHide={closeModal}>
      <Modal.Body>
        <div className="tw-p-6">
          <h2 className="tw-text-xl tw-mb-6">Backfill options</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="tw-w-full">
            {availableAccounts.length > 1 && (
              <div className="tw-mb-4">
                <div className="tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1">Account</div>
                <div className="tw-relative tw-w-full">
                  <select
                    className="myselect tw-bg-gray-100 tw-border-transparent tw-cursor-pointer tw-w-full tw-rounded-md tw-border tw-py-2 tw-px-3 tw-pr-8"
                    value={String(safeIndex)}
                    onChange={(e) => setSelectedAccountIndex(parseInt(e.target.value, 10))}
                  >
                    {availableAccounts.map((acc, i) => (
                      <option key={i} value={String(i)}>
                        {accountPickerLabel(acc, i, showAccountEmail)}
                      </option>
                    ))}
                  </select>
                  <div className="tw-absolute tw-top-2 tw-right-1.5 tw-text-slate-400 tw-text-xl tw-pointer-events-none">
                    <HiChevronUpDown />
                  </div>
                </div>
              </div>
            )}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div className="tw-col-span-1">
                <MySelect
                  register={register}
                  name="backfillType"
                  errors={errors}
                  rules={{ required: true }}
                  options={backfillTypeOptions}
                />
              </div>

              <div className="tw-col-span-1">
                {backfillOptionsFormProps?.options && (
                  <MySelect
                    register={register}
                    name={backfillOptionsFormProps?.name}
                    errors={errors}
                    rules={backfillOptionsFormProps?.rules}
                    options={backfillOptionsFormProps?.options}
                  />
                )}

                {backfillOptionsFormProps?.placeholder && (
                  <MyInput
                    register={register}
                    name={backfillOptionsFormProps?.name}
                    errors={errors}
                    rules={backfillOptionsFormProps?.rules}
                    placeholder={backfillOptionsFormProps?.placeholder}
                  />
                )}
              </div>
            </div>

            <div className="tw-flex tw-justify-end tw-space-x-2">
              <Button variant="secondary" size="sm" className="tw-mt-4" onClick={closeModal}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" className="tw-mt-4" type="submit">
                Run Backfill
              </Button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  )
}
